import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { Category, fetchExploreProperty, Provider } from '../api/adserver';
import * as Constant from '../lib/constants';

const debug = require('debug')('advertCategorySlice');

import {
  getCategories,
  getGatedCategories,
  Image as AdvertImage,
  // getThumbtackCategories,
  FullThumbtackCategory,
  // ThumbtackCategoriesObject,
  ExploreProperty,
  PropertyType,
  getThumbtackHomecare,
  ThumbtackHomecareResponse,
  ThumbtackHomecare,
  // ThumbtackCategoriesResponse,
  ThumbtackPropertyTypes,
} from '../api/adserver';

import { RootState } from './rootReducer';
import { AppThunk } from './store';

export enum ExploreCategoryType {
  ExploreInternal = 'ExploreInternal',
  Thumbtack = 'Thumbtack',
}

export interface AdvertCategory {
  id: string;
  title: string;
  image: AdvertImage;
  age_gated_min_age: null | number;
  category_type: ExploreCategoryType;
}

interface AdvertCategoryPayload {
  category: AdvertCategory | null;
}

type AdvertCategoriesPayload = AdvertCategory[];

// type ThumbackCagegoriesPayload = Array<ThumbtackCategoriesObject>;
type ThumbackCagegoriesPayload = Array<ThumbtackHomecare>;

interface AdvertCategories {
  categories: Array<AdvertCategory>;
  gatedCategories: Array<AdvertCategory>;
  current: AdvertCategory | null;
}

export type AdvertCategoriesState = {
  page: number;
  error: string | null;
  isLoading: boolean;
  // thumbtackCategories: Array<ThumbtackCategoriesObject>;
  thumbtackCategories: Array<ThumbtackHomecare>;
  isLoadingThumbtack: boolean;
} & AdvertCategories;

let initialState: AdvertCategoriesState = {
  page: 0,
  isLoading: false,
  error: null,
  categories: [], // testCategoryList,
  gatedCategories: [],
  current: null,
  thumbtackCategories: [],
  isLoadingThumbtack: false,
};

const getCategoriesDone = (state: AdvertCategoriesState) => {
  state.isLoading = false;
};

const advertCategoriesSlice = createSlice({
  name: 'advertCategories',
  initialState,
  reducers: {
    displayCategory(state, action: PayloadAction<AdvertCategoryPayload>) {
      const { category } = action.payload;
      state.current = category;
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    getCategoriesSuccess(
      state,
      action: PayloadAction<AdvertCategoriesPayload | any>,
    ) {
      getCategoriesDone(state);
      state.categories = action.payload;
      state.error = null;
    },
    getCategoriesFailed(state, action: PayloadAction<string>) {
      getCategoriesDone(state);
      state.categories = [];
      state.error = action.payload;
    },
    getCategoriesStart(state) {
      state.isLoading = true;
    },
    getGatedCategoriesStart(state) {
      state.isLoading = true;
    },
    getGatedCategoriesFailed(state, action: PayloadAction<string>) {
      getCategoriesDone(state);
      state.gatedCategories = [];
      state.error = action.payload;
    },
    getGatedCategoriesSuccess(
      state,
      action: PayloadAction<AdvertCategoriesPayload | any>,
    ) {
      getCategoriesDone(state);
      state.gatedCategories = action.payload;
      state.error = null;
    },
    getThumbtackCategoriesSuccess(
      state,
      action: PayloadAction<ThumbackCagegoriesPayload>,
    ) {
      state.isLoadingThumbtack = false;
      state.thumbtackCategories = action.payload;
      state.error = null;
    },
    getThumbtackCategoriesFailed(state, action: PayloadAction<string>) {
      state.isLoadingThumbtack = false;
      state.thumbtackCategories = [];
      state.error = action.payload;
    },
    getThumbtackCategoriesStart(state) {
      state.isLoadingThumbtack = true;
    },
  },
});

export const {
  displayCategory,
  setCurrentPage,
  getCategoriesSuccess,
  getCategoriesFailed,
  getCategoriesStart,
  getGatedCategoriesStart,
  getGatedCategoriesFailed,
  getGatedCategoriesSuccess,
  getThumbtackCategoriesStart,
  getThumbtackCategoriesSuccess,
  getThumbtackCategoriesFailed,
} = advertCategoriesSlice.actions;

export interface AppAdvertCategory {
  id: string;
  title: string;
  icon?: string;
  image: AdvertImage;
  age_gated_min_age: number;
  category_type: string;
}

export const selectCategories = createSelector(
  (state: RootState) => state.advertCategories,
  (categorySlice: AdvertCategoriesState) => {
    // debug('selectCategories are:');
    // debug(categorySlice.categories);
    const { isLoading, categories, gatedCategories } = categorySlice;
    if (!categories) {
      return {
        advertCategories: [] as Array<AppAdvertCategory>,
        gatedCategories,
        categories,
        isLoading,
      };
    }
    const advertCategories = categories.map((category: AdvertCategory) => {
      return {
        id: category.id,
        title: category.title,
        icon: category.image ? category.image.url : null,
        image: category.image,
        age_gated_min_age: category.age_gated_min_age,
        category_type: category.category_type,
      } as AppAdvertCategory;
    });
    const advertGatedCategories = gatedCategories
      ? gatedCategories.map((category: AdvertCategory) => {
          return {
            id: category.id,
            title: category.title,
            icon: category.image ? category.image.url : null,
            image: category.image,
            age_gated_min_age: category.age_gated_min_age,
            category_type: category.category_type,
          } as AppAdvertCategory;
        })
      : [];

    let allCategories = categories.map((cat) => cat);
    if (gatedCategories && gatedCategories.length > 0) {
      allCategories.push(...gatedCategories);
    }
    advertCategories.push(...advertGatedCategories);
    return {
      advertCategories,
      gatedCategories,
      categories: allCategories,
      isLoading,
    };
  },
);

export const selectThumbtackCategories = createSelector(
  (state: RootState) => {
    const { categories } = selectCategories(state);
    return {
      categories,
      thumbtackCategories: state.advertCategories.thumbtackCategories,
    };
  },
  ({
    categories,
    thumbtackCategories,
  }: {
    categories: AdvertCategory[];
    // thumbtackCategories: ThumbtackCategoriesObject[];
    thumbtackCategories: ThumbtackHomecare[];
  }) => {
    const thumbExploreCategory = categories.find((category) => {
      if (category.category_type === ExploreCategoryType.Thumbtack) {
        return true;
      }
    });
    const thumbtackCategory: FullThumbtackCategory = {
      id: Number(thumbExploreCategory?.id),
      title: thumbExploreCategory?.title,
      adverts: thumbtackCategories,
    };
    return {
      thumbtackCategory,
    };
  },
);

export const thumbtackEnabled = (categories: Category[]) => {
  return categories.some(
    (category) => category.category_type === Provider.Thumbtack,
  );
};

export const fetchAdvertCategories =
  (building: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getCategoriesStart());
      dispatch(getGatedCategoriesStart());
      const categories = await getCategories(building);
      const gatedCategoreis = await getGatedCategories(building);
      dispatch(getCategoriesSuccess(categories));
      dispatch(getGatedCategoriesSuccess(gatedCategoreis));
      console.log(`fetched categories from explore building: ${building}`);
      // console.log(
      //   `fetched categories from explore building: ${building}: ${JSON.stringify(categories, null, 2)}`,
      // );
      if (thumbtackEnabled(categories)) {
        console.log('Thumbtack is enabled');
        // check that we have postal code.  if not we can set it from the property
        const postalZipCode = await AsyncStorage.getItem(Constant.ZIP_POSTAL);
        if (Platform.OS === 'web' || !postalZipCode) {
          console.warn('using explore property postal code');
          // get the zipcode data for the building
          const property = await fetchExploreProperty(building);
          if (property) {
            console.log(
              `zipcode property loaded.  data is: ${JSON.stringify(property)}`,
            );
            if (property.postal_code) {
              await AsyncStorage.setItem(
                Constant.ZIP_POSTAL,
                property.postal_code,
              );
            } else {
              console.error('unable to determine zipcode from property data');
            }
          } else {
            console.warn(
              `could not load property for zipcode lookup: ${building}`,
            );
          }
        }
        dispatch(fetchFullThumbtackCategories());
      } else {
        console.log('Thumbtack is not enabled');
      }
    } catch (err: unknown) {
      console.warn('error loading advert categories for building:', building);
      let message = 'unknown error';
      if (err instanceof Error) {
        message = err.toString();
      }
      console.log(message);
      dispatch(getGatedCategoriesFailed(message));
      return dispatch(getCategoriesFailed(message));
    }
  };

// property should contain zip_code and or buildingCode.
// if no property name then set it to buildingCode
// :Promise<FullThumbtackCategory>
export const fetchFullThumbtackCategories =
  (): AppThunk => async (dispatch) => {
    try {
      dispatch(getThumbtackCategoriesStart());

      const condoCorpName = await AsyncStorage.getItem(
        Constant.CONDO_CORP_NAME,
      );
      let exploreProperty;
      if (condoCorpName) {
        exploreProperty = (await fetchExploreProperty(
          condoCorpName,
        )) as ExploreProperty;
        console.log(`exploreProperty ${exploreProperty}`);
        if (!exploreProperty) {
          exploreProperty = (await fetchExploreProperty(
            'MTCC3275',
          )) as ExploreProperty;
          console.log(`exploreProperty ${exploreProperty}`);
        }
        console.dir(exploreProperty);
      }

      const getExploreProperty = await AsyncStorage.getItem(
        Constant.EXPLORE_PROPERTY,
      );
      if (!getExploreProperty) {
        await AsyncStorage.setItem(
          Constant.EXPLORE_PROPERTY,
          JSON.stringify(exploreProperty),
        );
      }

      let categories:
        | ThumbtackHomecareResponse
        // | ThumbtackCategoriesResponse
        | undefined;

      if (
        exploreProperty &&
        exploreProperty.property_type === PropertyType.apartment
      ) {
        categories = await getThumbtackHomecare(
          ThumbtackPropertyTypes.apartment,
        );
        console.log(
          'Thumbtack homecare loaded.  category count is:',
          categories?.results.length,
        );
        if (!categories) {
          throw 'No Thumbtack homecare data';
        }

        dispatch(getThumbtackCategoriesSuccess(categories.results));
      } else {
        // Right now all properties that are not apartments will be other. This should be changed later to accommodate thumbtack's property types
        categories = await getThumbtackHomecare(ThumbtackPropertyTypes.other);
        // categories = await getThumbtackCategories();

        console.log(
          'Thumbtack categories loaded.  category count is:',
          categories?.results.length,
        );
        if (!categories) {
          throw 'No Thumbtack categories data';
        }
        dispatch(getThumbtackCategoriesSuccess(categories.results));
      }
    } catch (err: unknown) {
      console.warn('error loading thumbtack categories');
      let message = 'unknown error';
      if (err instanceof Error) {
        message = err.toString();
      }
      console.log(message);
      return dispatch(getThumbtackCategoriesFailed(message));
    }
  };

export default advertCategoriesSlice.reducer;
